import * as React from "react";
import { Props } from "react-select";
import { useQuery } from "react-query";
import axios from "axios";
import Field from "components/field";
import { useTranslation } from "react-i18next";

type TProps = Omit<Props, "value"> & {
  taxOnly?: boolean;
};

const TaxSelectSettings = (props: TProps) => {
  const { t } = useTranslation();
  const tBase = "components.react-select";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const fetchTaxOptions = useQuery(
    "taxOptions",
    async () =>
      await axios
        .get(
          `
      ${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/both
      `
        )
        .then((res) => res.data)
  );

  const taxData = fetchTaxOptions.data;
  const allOptions = React.useMemo(
    () => [...(taxData?.taxes || []), ...(taxData?.taxGroups || [])],
    [taxData]
  );

  const filteredOptions = React.useMemo(() => {
    return allOptions.filter((item: any) => {
      if (props.taxOnly) {
        if (item.tax_type === "tax") {
          return true;
        }
        if (item.taxes && Array.isArray(item.taxes)) {
          return item.taxes.every((tax: any) => tax.tax_type === "tax");
        }
        return false;
      }
      return true;
    });
  }, [allOptions, props.taxOnly]);

  const sortedOptions = React.useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const isCombinedGroup = (item: any) =>
      Array.isArray(item.taxes) &&
      item.taxes.length > 0 &&
      item.taxes.every((tax: any) => tax.tax_type === "tax");

    const mapped = (filteredOptions || []).map((item: any) => ({
      ...item,
      value: item.id,
      label: item.taxes && Array.isArray(item.taxes) ? `${item.label} (Group)` : item.label,
      sortLabel: item.label,
    }));

    const nonCombined = mapped.filter((item: any) => !isCombinedGroup(item));
    const combined = mapped.filter((item: any) => isCombinedGroup(item));
    const byLabel = (a: any, b: any) => collator.compare(a.sortLabel ?? a.label ?? "", b.sortLabel ?? b.label ?? "");

    return nonCombined.sort(byLabel).concat(combined.sort(byLabel));
  }, [filteredOptions]);

  if (!fetchTaxOptions.isSuccess) return null;

  const formatVal = allOptions.find((item: any) => item.id === props.value) || "";

  return (
    <Field
      type="select"
      style={{ width: "100%" }}
      options={filteredOptions?.map((item: any) => ({
        ...item,
        value: item.id,
        label: item.taxes && Array.isArray(item.taxes) ? `${item.label} (Group)` : item.label,
      }))}
      name="select"
      placeholder={tr("Select a tax")}
      {...props}
      value={formatVal}
    />
  );
};

export default TaxSelectSettings;